import type { FieldSpecOf } from 'v5/core/entity-schema/entitySchema.types';
import type {
  FieldCodec,
  LegacyMeasurementValue
} from 'v5/core/entity-values/values.types';
import { customFieldColumns } from './customFieldColumns';
import { findCustomFieldRowForRead } from './customFieldRows';
import { validateField, validation } from './validation';

/** First unit of the definition's list, as legacy `MeasurementInput` defaulted. */
export function measurementLegacyDefaultUnit(
  spec: FieldSpecOf<'measurement-legacy'>
): string {
  return spec.units[0]?.value ?? '';
}

function isEmpty(value: LegacyMeasurementValue): boolean {
  return value.amount === null;
}

/** Legacy MEASUREMENT custom field: `integer_value` amount + `string_value` unit (legacy `MEASUREMENT.jsx`). */
export const measurementLegacyCodec: FieldCodec<'measurement-legacy'> = {
  decode(entity, spec, ctx) {
    const row = findCustomFieldRowForRead(entity, spec.definition.id, ctx);
    const unit = row?.string_value ?? '';
    return {
      amount: row?.integer_value ?? null,
      unit: unit === '' ? measurementLegacyDefaultUnit(spec) : unit
    };
  },
  encode(value) {
    return {
      type: 'custom',
      columns: customFieldColumns({
        integer_value: value.amount,
        string_value: value.unit === '' ? null : value.unit
      })
    };
  },
  empty(spec) {
    return { amount: null, unit: measurementLegacyDefaultUnit(spec) };
  },
  isEmpty,
  validate(value, spec) {
    return validateField(value, spec, isEmpty, value => {
      if (value.amount !== null && !Number.isFinite(value.amount)) {
        return validation.invalid({ code: 'not-a-number', at: 'amount' });
      }
      // Stored in `integer_value`: the server rejects a fraction.
      if (value.amount !== null && !Number.isInteger(value.amount)) {
        return validation.invalid({ code: 'whole-number', at: 'amount' });
      }
      if (value.amount !== null && value.unit === '') {
        return validation.invalid({
          code: 'part-required',
          part: 'unit',
          at: 'unit'
        });
      }
      return validation.valid();
    });
  }
};
